import React, { useEffect, useRef, useState, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import {
  DNABox,
  DNABoxProps,
  DNAButton,
  DNAContextMenu,
  DNAText,
  Iffy,
  DNADivider,
  DNACheckbox,
  util,
  DNAConditionalWrapper,
  DNAButtonGroup,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, DNAPopover } from 'src/components/DNA';
import { DocumentVersionORM } from 'src/types/orms';
import {
  GroupStatus,
  ModifiedPayloadGroup,
  unavailableStatus,
  usePresentationBuilderState,
} from 'src/components/PresentationBuilder/state/PresentationBuilderStateProvider';
import CollapsibleCard, { CollapsibleActionMenu } from 'src/components/CollapsibleCard/CollapsibleCard';
import { thumbnailSizeDimensions } from 'src/hooks/useThumbnailSize/useThumbnailSize';
import { GroupItemOverlay, isPresentationBuilderGroupHidden } from './Overlays';
import Slide from './Slide';

interface GroupHeaderTitleProps {
  title?: string,
  documentVersionORM?: DocumentVersionORM
}

interface GroupHeaderActionItemsProps {
  group: ModifiedPayloadGroup
  isMutexLocked?: boolean,
  onCheck?: () => void,
  isChecked?: boolean,
}

const S = StyleSheet.create({
  greyBox: {
    width: '28%',
    height: '35%',
    backgroundColor: colors['color-gray-80'],
    margin: '1.5%',
    borderRadius: 0.5,
  },
  greyBoxContainer: {
    padding: '5%',
  },
  overlayHeader: {
    paddingHorizontal: 15,
    paddingVertical: 10,
  },
  selected: {
    borderColor: colors['color-brand2-500'],
    borderRadius: 6,
    borderWidth: 3,
  },
  successCollapsibleCard: {
    minHeight: 'fit-content',
    borderRadius: 4,
    borderWidth: 3,
    borderColor: colors['color-success-500'],
  },
  warningCollapsibleCard: {
    minHeight: 'fit-content',
    borderRadius: 4,
    borderWidth: 3,
    borderColor: colors['border-light-primary'],
  },
  selectedCollapsibleCard: {
    minHeight: 'fit-content',
    borderRadius: 4,
    borderWidth: 3,
    borderColor: colors['color-brand2-500'],
  },
  contextMenuItems: {
    top: 4,
  },
  popoverText: {
    color: colors['color-text-white'],
  },
})

const GroupHeaderTitle: React.FC<GroupHeaderTitleProps> = ({ title, documentVersionORM }) => {
  return (
    <DNABox fill appearance="col" alignY="center" style={{ width: 100 }}>
      <DNAText testID="group-header-title" h5 numberOfLines={1}>{title ?? 'Untitled Group'}</DNAText>
      <DNAText c1 status="tertiary" numberOfLines={1}>{documentVersionORM?.model.title ?? 'Untitled Document'}</DNAText>
    </DNABox>
  )
}

export const GroupHeaderActionItems: React.FC<GroupHeaderActionItemsProps> = ({
  group, isMutexLocked, onCheck, isChecked,
}) => {
  const {
    toggleHiddenPage,
    toggleGroupVisibility,
    openFindReplacement,
    onRemoveGroup,
    onRemoveAllUnavailableGroups,
    onSeparateGroup,
  } = usePresentationBuilderState()
  const isUnavailable = !!unavailableStatus[group.groupStatus];
  const isFindAndReplace = group.groupStatus === GroupStatus.MAJOR_UPDATE;
  const isMinorWithAutoMappingSlides = group.groupStatus === GroupStatus.MINOR_UPDATE_WITH_AUTO_MAPPING_SLIDES;

  const onHide = () => {
    const singlePage = group.pages.length === 1 ? group.pages[0] : undefined;

    if (singlePage?.pageId) {
      toggleHiddenPage(group.id, singlePage.pageId);
      return;
    }

    toggleGroupVisibility(group.id);
  }

  if (isUnavailable || isFindAndReplace || isMinorWithAutoMappingSlides) {
    return (
      <DNABox spacing="sm" alignY="center">
        <Iffy is={isMinorWithAutoMappingSlides}>
          <DNAButton
            testID="review-or-replace-button"
            appearance="outline"
            status="tertiary"
            onPress={() => openFindReplacement(group)}
            children="Review or replace"
          />
          <DNAButton
            status="transparent"
            iconLeft="trash"
            onPress={() => onRemoveGroup(group)}
          />
        </Iffy>
        <Iffy is={isFindAndReplace}>
          <DNAButton
            testID="find-and-replace-button"
            status="warning"
            iconLeft="arrows-clockwise"
            iconStyle={{ marginRight: 4 }}
            onPress={() => openFindReplacement(group)}
            children="Find replacement"
          />
          <DNAButton
            status="transparent"
            iconLeft="trash"
            onPress={() => onRemoveGroup(group)}
          />
        </Iffy>
        <Iffy is={isUnavailable}>
          <DNAButtonGroup>
            <DNAButton
              status="warning"
              iconLeft="trash"
              iconStyle={{ marginRight: 4 }}
              onPress={() => onRemoveGroup(group)}
              children="Remove"
            />
            <DNAContextMenu style={S.contextMenuItems}>
              <DNAContextMenu.Anchor>
                <DNAButton
                  onPress={() => { }}
                  iconLeft="caret-down"
                  status="warning"
                />
              </DNAContextMenu.Anchor>
              <DNAContextMenu.Items>
                <DNAContextMenu.Item
                  collapseOnPress
                  delay={100}
                  title="Remove all unavailable slides"
                  onPress={onRemoveAllUnavailableGroups}
                />
              </DNAContextMenu.Items>
            </DNAContextMenu>
          </DNAButtonGroup>
        </Iffy>
      </DNABox>
    )
  }
  else {
    return (
      <DNABox spacing="sm" alignY="center">
        <DNAChip.Beacon children={`${group.pages.length}`} />
        <DNAConditionalWrapper
          condition={!group.locked}
          wrapper={children => (
            <DNAPopover placement="top" disablePopover={['tabletPWA']}>
              <DNAPopover.Anchor>
                {children}
              </DNAPopover.Anchor>
              <DNAPopover.Content>
                <DNAText style={S.popoverText}>Ungroup</DNAText>
              </DNAPopover.Content>
            </DNAPopover>
          )}
        >
          <DNAButton
            appearance="ghost"
            status="subtle"
            iconLeft={group.locked ? 'lock' : 'cards'}
            disabled={group.locked}
            onPress={() => onSeparateGroup(group)}
          />
        </DNAConditionalWrapper>
        <Iffy is={!isMutexLocked}>
          <DNAContextMenu>
            <DNAContextMenu.Anchor>
              <DNAButton
                appearance="ghost"
                status="subtle"
                iconLeft="dots-three-vertical-bold"
              />
            </DNAContextMenu.Anchor>
            <DNAContextMenu.Items>
              <DNAContextMenu.Item
                title={isPresentationBuilderGroupHidden(group) ? 'Unhide' : 'Hide'}
                icon={isPresentationBuilderGroupHidden(group) ? 'eye' : 'eye-slash'}
                onPress={onHide}
              />
              <DNAContextMenu.Item
                title="Remove"
                icon="trash"
                onPress={() => onRemoveGroup(group)}
              />
            </DNAContextMenu.Items>
          </DNAContextMenu>
        </Iffy>
        <DNACheckbox
          status="gray"
          onChange={onCheck}
          checked={isChecked}
        />
      </DNABox>
    )
  }
}

const GroupedSlides: React.FC<{
    group: ModifiedPayloadGroup,
    startingIdx: number,
    orientation: DNABoxProps['appearance'],
    isDraggingMode: boolean,
    islocked?: boolean,
    isCollapsed?: boolean,
    onCheck?: () => void,
    isChecked?: boolean,
  }> = (props) => {
    const {
      group, orientation, startingIdx, isDraggingMode, islocked, onCheck, isChecked,
    } = props
    const { editorThumbnailSize, autoUpdateMapping } = usePresentationBuilderState()

    const newMappingGroupSrcId = autoUpdateMapping[group.id];
    const isUnavailable = !!unavailableStatus[group.groupStatus];
    const isFindAndReplace = group.groupStatus === GroupStatus.MAJOR_UPDATE;
    const isMinorWithAutoMappingSlides = !!newMappingGroupSrcId &&
      group.groupStatus === GroupStatus.MINOR_UPDATE_WITH_AUTO_MAPPING_SLIDES;

    const latestPublishedDocumentVersionORM = group.documentVersionORM?.relations.documentORM
      .relations.version.latestPublishedDocumentVersionORM;
    const targetMappingGroup = latestPublishedDocumentVersionORM?.model.pageGroups
      ?.find(pageGroup => pageGroup.id === newMappingGroupSrcId);
    const displayedDocumentVersionORM = isMinorWithAutoMappingSlides && targetMappingGroup
      ? latestPublishedDocumentVersionORM
      : group.documentVersionORM;
    const displayedGroupTitle = isMinorWithAutoMappingSlides && targetMappingGroup
      ? targetMappingGroup.name
      : group.name

    const thumbnailDimensions = useMemo(() => {
      return {
        height: thumbnailSizeDimensions[editorThumbnailSize].height,
        width: thumbnailSizeDimensions[editorThumbnailSize].width,
      }
    }, [editorThumbnailSize])
    const draggingOverlayStyle = useMemo(() => StyleSheet.create([
      {
        borderRadius: 4,
        borderWidth: 1,
        borderColor: colors['color-gray-100'],
        marginTop: 4,
        marginBottom: 18,
        backgroundColor: colors['color-text-basic'],
        height: thumbnailDimensions.height + 14,
        width: thumbnailDimensions.width + 14,
      },
    ]), [thumbnailDimensions])

    const [isCollapsed, setIsCollapsed] = useState(props.isCollapsed ?? false)
    const currentCollapsedState = useRef<undefined | boolean>(false)

    useEffect(() => {
      // for the ui we wan to drag and drop on collapsed mode to facilitate the drag and drop interaction
      if (isDraggingMode) {
        setIsCollapsed(true)
      }
      // this means the drag and drop finished, so we need to reset the collapse state
      else if ( !isDraggingMode && currentCollapsedState.current !== undefined) {
        setIsCollapsed(currentCollapsedState.current)
      }
    }, [isDraggingMode])

    const cardStyle = util.mergeStyles(
      undefined,
      [S.warningCollapsibleCard, !group.visible || isUnavailable || isFindAndReplace],
      [S.successCollapsibleCard, isMinorWithAutoMappingSlides],
      [S.selectedCollapsibleCard, isChecked],
    )

    return (
      <DNABox fill>
        <Iffy is={isDraggingMode}>
          <DNABox appearance="col" style={draggingOverlayStyle}>
            {/* header */}
            <DNABox style={S.overlayHeader} spacing="between" alignY="center">
              <GroupHeaderTitle title={displayedGroupTitle} documentVersionORM={displayedDocumentVersionORM}/>
              <DNAChip.Beacon children={`${group.pages.length}`} />
            </DNABox>
            <DNADivider />
            {/* content */}
            <DNABox fill style={S.greyBoxContainer} wrap="start" alignX="center" alignY="center">
              <DNABox style={S.greyBox} />
              <DNABox style={S.greyBox} />
              <DNABox style={S.greyBox} />
              <DNABox style={S.greyBox} />
              <DNABox style={S.greyBox} />
              <DNABox style={S.greyBox} />
            </DNABox>
          </DNABox>
        </Iffy>
        <Iffy is={!isDraggingMode}>
          <GroupItemOverlay
            group={group}
            enabled
            isLocked={props.islocked}
          >
            <CollapsibleCard
              headerTitle={
                <GroupHeaderTitle title={displayedGroupTitle} documentVersionORM={displayedDocumentVersionORM}/>
              }
              isCollapsed={isCollapsed}
              onToggleChanged={(isCollapsed) => {
                currentCollapsedState.current = isCollapsed
              }}
              cardStyle={cardStyle}
            >
              <CollapsibleActionMenu>
                <GroupHeaderActionItems
                  onCheck={onCheck}
                  isChecked={isChecked}
                  isMutexLocked={islocked}
                  group={group}
                />
              </CollapsibleActionMenu>
              <DNABox
              // [NOTE] - This might be achievable without a prop by normal flexing
              //  but flexing certain containers might mess with DND
                appearance={orientation}
                spacing="sm"
                wrap="stretch"
              >
                {
                group.pages.map((page, idx) => (
                  <DNABox appearance="col" key={`${group.id}-${page.pageId}-${idx}`} style={{ marginBottom: 24 }}>
                    <Slide
                      group={group}
                      page={page}
                      displayIdx={startingIdx ? (startingIdx + idx) : -1}
                      enableOverlay={group.pages.length > 1}
                    />
                  </DNABox>
                ))
              }
              </DNABox>
            </CollapsibleCard>
          </GroupItemOverlay>
        </Iffy>
      </DNABox>
    )
  }

export default GroupedSlides
